import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  OnDestroy,
  output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {interval, Observable, of, take} from 'rxjs';
import {filter} from 'rxjs/operators';

import {DynamicFormConfig, FormSnapshot} from '../../models/form-config.model';
import {DynamicFormService} from '../../services/dynamic-form.service';
import {FormDraftService} from '../../services/form-draft.service';
import {CategoryAccordionComponent} from '../category-accordion/category-accordion.component';
import {ModalService} from '../../../../services/modal/modal-service';
import {AcceptancesBlockComponent} from '../controls/acceptances-block/acceptances-block.component';
import {DtActionsService, TableActionsMobileMode} from '../../../../widgets/data-table/services/dt-actions.service';
import {TableAction} from '../../../../widgets/table-actions/table-action.model';

/**
 * DynamicFormComponent — entry point dell'engine.
 *
 * Responsabilità:
 * - Riceve la DynamicFormConfig e il prefill opzionale
 * - Fornisce DynamicFormService come istanza locale (providers[])
 * - Gestisce auto-draft e submit finale
 * - Gestisce il focus sul primo campo quando una categoria viene aperta
 * - Espone canDeactivate() per la guard UnsavedChanges
 * - Include AcceptancesBlockComponent prima del footer
 * - submit() chiama markAcceptancesTouched() per mostrare errori sulle accettazioni
 *
 * Utilizzo:
 * ```html
 * <app-dynamic-form
 *   [config]="anagraficaConfig"
 *   [prefill]="datiDalBackend$ | async"
 *   (formSubmit)="onSubmit($event)"
 * />
 * ```
 */
@Component({
  selector: 'app-dynamic-form',
  standalone: true,
  imports: [ReactiveFormsModule, CategoryAccordionComponent, AcceptancesBlockComponent],
  providers: [DynamicFormService],
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss',
})
export class DynamicFormComponent implements OnInit, OnDestroy {

  config = input.required<DynamicFormConfig>();

  /**
   * Valori di prefill dal backend.
   * Se presente anche un draft salvato, il draft ha la precedenza
   * (a meno che autoLoadDraft sia false nella config).
   */
  prefill = input<FormSnapshot>();

  /**
   * Modalità layout mobile della barra azioni.
   * - 'dropdown' (default): le azioni sono collassate nel pulsante "Azioni"
   * - 'buttons': i pulsanti sono visibili anche su mobile
   */
  mobileActionMode = input<TableActionsMobileMode>();

  /**
   * Se false, non registra l'azione "Salva" nella barra azioni.
   * Utile quando il parent gestisce il proprio pulsante di submit (es. modale primo accesso).
   */
  showSubmitAction = input<boolean>(true);

  /**
   * Azioni aggiuntive da mostrare nella barra azioni del form
   * (es. "Elimina" nella scheda utente di studio), inserite dopo "Salva".
   * Il parent ne gestisce la logica.
   */
  extraActions = input<TableAction[]>([]);

  /**
   * Se true, il pulsante "Salva" è sempre abilitato (la validazione avviene
   * al click). Default false: "Salva" è abilitato solo se il form è
   * modificato e valido. Utile per schede in cui il Salva deve essere
   * sempre attivo (es. studio_utenti_scheda §5.1).
   */
  submitAlwaysEnabled = input<boolean>(false);

  /** True quando il form è dirty e valido, quindi pronto per il submit. */
  readonly canSubmit = computed(() => this.svc.isDirty() && this.svc.isValid());

  /** Emette il valore completo del form dopo un submit valido. */
  readonly formSubmit = output<FormSnapshot>()

  protected readonly svc = inject(DynamicFormService);
  private readonly draft = inject(FormDraftService);
  private readonly actionsService = inject(DtActionsService);
  private readonly modal = inject(ModalService);
  private readonly destroy = inject(DestroyRef);
  private readonly injector = inject(Injector);

  @ViewChildren(CategoryAccordionComponent)
  private accordionRefs!: QueryList<CategoryAccordionComponent>;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  constructor() {
    // Quando l'utente apre una categoria (o all'avvio, la prima), sposta il
    // focus sul suo primo campo. effect() qui è equivalente a un watcher sul
    // signal: si riesegue ogni volta che lastOpenedKey() emette un nuovo valore.
    effect(() => {
      const key = this.svc.lastOpenedKey();
      if (!key) return;
      // setTimeout garantisce che il DOM dell'accordion sia già aggiornato
      setTimeout(() => {
        const target = this.accordionRefs.find(a => a.categoryKey === key);
        target?.focusFirstField();
      }, 300);
    });

    // Aggiorna lo stato del pulsante Invia in base alla validità e alle modifiche effettive del form
    effect(() => {
      const isDisabled = !this.svc.isDirty() || !this.svc.isValid();
      this.setupActions(isDisabled);
    });
  }

  ngOnInit(): void {
    const config = this.config();
    const autoLoad = config.autoLoadDraft ?? true;
    const savedDraft = autoLoad ? this.draft.loadDraft(config.formId) : null;

    if (savedDraft) {
      // Draft trovato: init immediato, prefill ignorato (priorità al draft)
      this.initForm(savedDraft);
    }
    else {
      // Inizializzazione immediata col valore corrente (anche se undefined o null)
      this.initForm(this.prefill());

      // Se il prefill era assente (null o undefined), restiamo in ascolto del primo prefill
      // valido che potrebbe arrivare in asincrono (es. via async pipe)
      if (this.prefill() == null) {
        toObservable(this.prefill, {injector: this.injector}).pipe(
          filter((v): v is FormSnapshot => v != null),
          take(1),
          takeUntilDestroyed(this.destroy),
        ).subscribe(prefillValue => this.initForm(prefillValue));
      }
    }

    this.setupAutoDraft(config);
    this.setupActions();
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  submit(): void {
    const group = this.svc.rootGroup();
    if (!group) return;

    if (group.invalid) {
      // Mostra errori su tutti i campi delle categorie
      group.markAllAsTouched();
      // Mostra errori specifici sulle accettazioni non spuntate
      this.svc.markAcceptancesTouched();
      return;
    }

    const value = this.svc.getFormValue();
    this.draft.clearDraft(this.config().formId);
    this.svc.resetDirty();
    this.formSubmit.emit(value);
  }

  private initForm(snapshot?: FormSnapshot): void {
    this.svc.init(this.config(), snapshot);
  }

  private setupAutoDraft(config: DynamicFormConfig): void {
    const intervalMs = config.draftIntervalMs ?? 30_000;
    if (intervalMs <= 0) return;

    interval(intervalMs).pipe(
      takeUntilDestroyed(this.destroy),
      filter(() => this.svc.isDirty()),
    ).subscribe(() => {
      this.draft.saveDraft(config.formId, this.svc.getFormValue());
    });
  }

  private setupActions(submitDisabled = true): void {
    const mode = this.mobileActionMode();
    if (mode) {
      this.actionsService.setMobileMode(mode);
    }

    const actions: TableAction[] = [];

    if (this.showSubmitAction()) {
      actions.push({
        id: 'save',
        label: 'Salva',
        svgIcon: 'icon/save.svg',
        isDisabled: this.submitAlwaysEnabled() ? false : submitDisabled,
        action: () => this.submit(),
      });
    }

    // Azioni aggiuntive fornite dal parent (es. "Elimina").
    actions.push(...this.extraActions());

    this.actionsService.setActions(actions);
  }

  // ── Guard ────────────────────────────────────────────────────────────────

  /**
   * Chiamato dalla guard unsavedChangesGuard sul componente pagina.
   * Se il form è dirty apre una modale di conferma,
   * altrimenti lascia navigare liberamente.
   */
  canDeactivate(): Observable<boolean> {
    if (!this.svc.isDirty()) {
      this.actionsService.clearActions();
      return of(true);
    }

    const message =
      this.config().unsavedChangesMessage ??
      'Sei sicuro di voler uscire? Perderai tutti i dati compilati.';

    return new Observable<boolean>(observer => {
      this.modal.openModal({
        title: 'Attenzione',
        description: message,
        primaryButtonText: 'Esci',
        secondaryButtonText: 'Rimani',
        onConfirm: () => {
          observer.next(true);
          observer.complete();
          this.actionsService.clearActions();
        },
        onCancel: () => {
          observer.next(false);
          observer.complete();
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.actionsService.clearActions();
    this.actionsService.resetMobileMode();
  }

  // Metodo helper privato — non usato nel submit ma utile per debug
  private onlyCategoriesInvalid(): boolean {
    return false;
  }
}
